/*
  Жидкое тело компаньона: контур как радиальная функция (docs/observer-design.md §4.7).

  Сфера — замкнутый контур из N точек по кругу. Радиус каждой точки = базовый радиус,
  умноженный на (1 + сумма слагаемых). Каждое слагаемое — одно поведение:
    breathe — дыхание формы (низкая гармоника, покой);
    jelly   — желе, перетекание (несколько низких гармоник с разными фазами, «думает»);
    ripple  — рябь контура (оставлена для экспериментов, в пресетах выключена);
    flow    — поток: волны-рельеф по поверхности вверх или вниз (передача данных), рисует LiquidBody;
              где дуга выходит на край, контур вздувается — волна видна и в силуэте;
    stir    — перемешивание («думает»): бугры желе плывут по кругу в одну сторону, а внутри тела
              поворачивается вихрь-рельеф (завитки считает stirArm, рисует LiquidBody);
    spikes  — шипы феррожидкости: ёж при ошибке. Вид — прежний, на общей сетке точек (мягче и
              «жидче», чем иглы); когда и насколько выросли — огибающая реакции (LiquidFrame);
    pull    — выступ к курсору; сила зависит от расстояния: далеко — бугор, вплотную — нить;
    splash  — волна от брошенного камня (затухающее колебание после события).
  Точки соединяются замкнутой сглаженной кривой (Catmull-Rom → кубические Безье).
*/

export interface LiquidParams {
  /** Базовый радиус в единицах viewBox. */
  radius: number;
  breathe: { amp: number; speed: number };
  jelly: { amp: number; speed: number };
  ripple: { amp: number; speed: number; dir: -1 | 0 | 1 };
  /**
   * Поток: волны-рельеф, бегущие по поверхности вниз (1) или вверх (−1). width — толщина рельефа
   * дуги в единицах тела; edge — волна по краю: насколько дуга вздувает контур там, где выходит на край.
   */
  flow: { dir: -1 | 0 | 1; speed: number; strength: number; width: number; edge: number };
  /**
   * Перемешивание: dir 1 — по часовой, −1 — против, 0 — выключено (желе колышется на месте).
   * speed — скорость вихря (1 = оборот за 8 с), strength — сила рельефа 0…1, twist — закрутка в оборотах.
   */
  stir: { dir: -1 | 0 | 1; speed: number; strength: number; twist: number };
  /**
   * Шипы: count — сколько, amp — высота, sharp — острота, tremble — сила дрожи после выброса 0…1,
   * fall — за сколько секунд шипы уходят обратно в тело.
   */
  spikes: { count: number; amp: number; sharp: number; tremble: number; fall: number };
  /** angle — направление к курсору в радианах; strength — 0…1 после закона расстояния. */
  pull: { angle: number; strength: number };
  /** phase растёт от 0 после удара; amp — сила удара. */
  splash: { amp: number; phase: number };
}

export interface Pt {
  x: number;
  y: number;
}

/**
 * Что меняется по ходу реакции на событие (готово, шаг, ошибка) — считает LiquidBody по времени:
 * pulse — равномерное раздувание (доля радиуса), spikes — насколько выросли шипы 0…1+,
 * jitter — дрожь высоты шипов, settle — колыхание формы после того, как шипы опали.
 */
export interface LiquidFrame {
  pulse: number;
  spikes: number;
  jitter: number;
  settle: number;
}

export const STILL: LiquidFrame = { pulse: 0, spikes: 0, jitter: 0, settle: 0 };

export const POINTS = 48;

/** Поток: сколько дуг бежит по телу и за сколько секунд одна дуга проходит его целиком. */
export const FLOW_ARCS = 3;
export const FLOW_PERIOD_S = 2.4;
/** Ширина волны по краю по высоте, в долях радиуса: соседние дуги не сливаются в один бугор. */
const FLOW_EDGE_SIGMA = 0.26;

/**
 * Где сейчас дуги потока: высота в долях радиуса (−1 верх … 1 низ) и затухание у полюсов.
 * Одна функция и для рельефа (LiquidBody), и для волны по краю (outline): они всегда совпадают.
 */
export function flowArcs(flow: LiquidParams['flow'], t: number): Array<{ y: number; fade: number }> {
  if (flow.dir === 0 || flow.strength <= 0) return [];
  const arcs: Array<{ y: number; fade: number }> = [];
  for (let i = 0; i < FLOW_ARCS; i++) {
    const cycle = ((((t * flow.speed) / FLOW_PERIOD_S + i / FLOW_ARCS) % 1) + 1) % 1;
    const y = flow.dir === 1 ? -1 + cycle * 2 : 1 - cycle * 2;
    arcs.push({ y, fade: Math.max(0, 1 - y * y) });
  }
  return arcs;
}

/** Закон притяжения: сила = (1 − d / reach)², ноль за пределами радиуса действия. */
export function pullStrength(distance: number, reach: number) {
  if (reach <= 0 || distance >= reach) return 0;
  const k = 1 - distance / reach;
  return k * k;
}

/** Контур в момент времени t (секунды); f — состояние реакции на событие в этот момент. */
export function outline(p: LiquidParams, t: number, f: LiquidFrame = STILL): Pt[] {
  const pts: Pt[] = [];
  const spikeSharp = Math.max(1, p.spikes.sharp);
  // Шипы — прежний вид (решение Максима 2026-10-19): общая сетка точек сглаживает узкие шипы в
  // мягкие наплывы, и они медленно ползут — тело читается жидкостью, а не шаром с иглами.
  const spikeAmp = p.spikes.amp * f.spikes;
  // Острота выступа растёт с силой: далеко — широкий бугор, вплотную — нить.
  const pullSharp = 2 + 22 * p.pull.strength;
  const pullAmp = 0.65 * p.pull.strength;
  // Удар: контур только мягко вздыхает. Сами волны — концентрические круги внутри тела
  // (рисует LiquidBody), а не зубцы по краю: круги по воде идут от центра, не наружу.
  const ph = p.splash.phase;
  const swell = p.splash.amp > 0 ? p.splash.amp * 0.5 * Math.exp(-2.4 * ph) * Math.sin(6 * Math.PI * ph) : 0;
  // Поток: там, где бегущая дуга выходит на край, контур вздувается по обе стороны.
  const arcs = p.flow.edge > 0 ? flowArcs(p.flow, t) : [];
  const edgeAmp = p.flow.edge * p.flow.strength;

  for (let i = 0; i < POINTS; i++) {
    const th = (i / POINTS) * Math.PI * 2;
    const yNorm = Math.sin(th); // высота точки на контуре, −1…1

    let k = 1;
    if (p.stir.dir === 0) {
      k += p.breathe.amp * Math.sin(2 * th + t * p.breathe.speed);
      // Бугры бегут навстречу друг другу: форма колышется на месте.
      k +=
        p.jelly.amp *
        (0.6 * Math.sin(3 * th + t * p.jelly.speed) + 0.4 * Math.sin(4 * th - t * p.jelly.speed * 0.7));
    } else {
      // Перемешивание: всё плывёт в одну сторону, но с разной скоростью, а размах бугров
      // медленно гуляет — капля перекатывается, а не вращается жёстко, как шестерёнка.
      const w = p.stir.dir * t * p.jelly.speed;
      k += p.breathe.amp * Math.sin(2 * th - p.stir.dir * t * p.breathe.speed);
      k +=
        p.jelly.amp *
        (0.6 * (0.8 + 0.2 * Math.sin(t * 0.37)) * Math.sin(3 * th - w) +
          0.4 * (0.8 + 0.2 * Math.sin(t * 0.53 + 1)) * Math.sin(4 * th - 0.7 * w));
    }
    if (p.ripple.dir !== 0) {
      // Горизонтальные гребни, бегущие вверх или вниз по поверхности.
      k += p.ripple.amp * Math.sin(yNorm * 5.5 - p.ripple.dir * t * p.ripple.speed);
    }
    for (const a of arcs) {
      const dy = (yNorm - a.y) / FLOW_EDGE_SIGMA;
      k += edgeAmp * a.fade * Math.exp(-dy * dy);
    }
    if (spikeAmp > 0 && p.spikes.count > 0) {
      const phase = p.spikes.count * th + t * 0.6;
      const c = Math.max(0, Math.cos(phase));
      // Дрожь после выброса: у каждого шипа своя фаза, поэтому они не пульсируют хором.
      const jit = f.jitter
        ? 1 + f.jitter * Math.sin(Math.PI * 2 * 17 * t + 2.1 * Math.round(phase / (Math.PI * 2)))
        : 1;
      k += spikeAmp * jit * Math.pow(c, spikeSharp);
    }
    // Реакции: ровное раздувание («готово», «шаг») и колыхание после того, как шипы опали —
    // сначала вытягивается вверх, потом оседает.
    k += f.pulse;
    if (f.settle !== 0) k -= f.settle * Math.cos(2 * th);
    if (pullAmp > 0) {
      const c = Math.max(0, Math.cos(th - p.pull.angle));
      k += pullAmp * Math.pow(c, pullSharp);
    }
    if (swell !== 0) k += swell;

    const r = p.radius * k;
    pts.push({ x: Math.cos(th) * r, y: Math.sin(th) * r });
  }
  return pts;
}

/** Замкнутая сглаженная кривая через точки: Catmull-Rom, переведённый в кубические Безье. */
export function closedSmoothPath(pts: Pt[]): string {
  const n = pts.length;
  if (n < 3) return '';
  const f = (v: number) => v.toFixed(2);
  let d = `M ${f(pts[0].x)} ${f(pts[0].y)}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2.x)} ${f(p2.y)}`;
  }
  return d + ' Z';
}

/** Открытая сглаженная кривая через точки (завиток вихря): тот же Catmull-Rom, без замыкания. */
export function openSmoothPath(pts: Pt[]): string {
  const n = pts.length;
  if (n < 2) return '';
  const f = (v: number) => v.toFixed(2);
  let d = `M ${f(pts[0].x)} ${f(pts[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(n - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2.x)} ${f(p2.y)}`;
  }
  return d;
}

/**
 * Радиус живого контура в направлении th: между соседними точками — по прямой. Сетка точек
 * равномерная, но может начинаться не с нуля (у шипов), поэтому начало берём с первой точки.
 */
export function radiusAt(contour: Pt[], th: number) {
  const n = contour.length;
  const th0 = Math.atan2(contour[0].y, contour[0].x);
  const x = (((((th - th0) / (Math.PI * 2)) % 1) + 1) % 1) * n;
  const i = Math.floor(x) % n;
  const f = x - Math.floor(x);
  const a = contour[i];
  const b = contour[(i + 1) % n];
  return Math.hypot(a.x, a.y) * (1 - f) + Math.hypot(b.x, b.y) * f;
}

/**
 * Завиток вихря от центра к краю. Внутренний конец убегает вперёд по вращению — так
 * закручивается размешанная жидкость. Радиус берётся от живого контура в той же стороне,
 * поэтому завиток тянется вместе с формой. angle — поворот вихря, twist — закрутка в радианах,
 * reach — докуда завиток доходит в долях контура.
 */
export function stirArm(
  contour: Pt[],
  angle: number,
  twist: number,
  dir: number,
  reach: number,
  samples = 16,
) {
  const pts: Pt[] = [];
  for (let s = 0; s < samples; s++) {
    const u = s / (samples - 1);
    const th = angle + dir * twist * (1 - u);
    const r = (0.1 + (reach - 0.1) * u) * radiusAt(contour, th);
    pts.push({ x: Math.cos(th) * r, y: Math.sin(th) * r });
  }
  return pts;
}

export const CALM: LiquidParams = {
  radius: 40,
  breathe: { amp: 0.025, speed: 1.1 },
  jelly: { amp: 0, speed: 1.4 },
  ripple: { amp: 0, speed: 3.2, dir: 0 },
  flow: { dir: 0, speed: 1, strength: 1, width: 7, edge: 0.08 },
  stir: { dir: 0, speed: 1, strength: 0.8, twist: 0.9 },
  spikes: { count: 12, amp: 0.22, sharp: 8, tremble: 0.5, fall: 0.9 },
  pull: { angle: 0, strength: 0 },
  splash: { amp: 0.14, phase: 0 },
};

/** Пресеты поведения по состоянию: только форма; цвет и аура задаются отдельно. */
export const PRESETS: Record<string, Partial<LiquidParams>> = {
  idle: CALM,
  thinking: {
    ...CALM,
    breathe: { amp: 0.02, speed: 1.6 },
    jelly: { amp: 0.07, speed: 1.6 },
    stir: { dir: 1, speed: 1, strength: 0.8, twist: 0.9 },
  },
  writing: { ...CALM, breathe: { amp: 0.035, speed: 3.4 } },
  download: { ...CALM, flow: { ...CALM.flow, dir: 1 } },
  upload: { ...CALM, flow: { ...CALM.flow, dir: -1 } },
  update: { ...CALM, jelly: { amp: 0.05, speed: 0.9 }, breathe: { amp: 0.03, speed: 0.8 } },
  // «Готово» и «ошибка» — это реакции (разлив с пульсом, ёж), их играет LiquidBody по событию;
  // форма в самом состоянии спокойная.
  success: CALM,
  error: CALM,
};
